<template>
  <!-- NOTE: 入店契機マスタが無い、または担当にできる人物がいない店舗では区切り線ごと出さない -->
  <div v-if="referrals.length > 0 && users.length > 0">
    <v-divider class="my-3" />

    <v-card elevation="0">
      <v-card-title class="pb-1">
        <p class="pa-0 ma-0 text--secondary text-sm">
          {{ currentClub.referralAlias }}
        </p>
      </v-card-title>

      <!-- NOTE: 旧UIは歯車で表示/編集を切り替えていたが、開く一手間が無駄という
         現場フィードバックにより常時インライン編集にした。変更があるときだけ更新/キャンセルを出す -->
      <v-card-text>
        <!-- eslint-disable vue/v-on-event-hyphenation -->
        <referral-card
          v-for="(referringHistoryTemp, referringHistoryTempIdx) in referringHistoriesTemp"
          :key="`referringHistoriesTemp[${referringHistoryTempIdx}]`"
          :value="referringHistoryTemp"
          :referrals="referrals"
          :users="users"
          :referring-history-temp-idx="referringHistoryTempIdx"
          class="mb-4"
          @update:referralId="referringHistoryTemp.referralId = $event"
          @update:userId="referringHistoryTemp.userId = $event"
          @update:_destroy="referringHistoryTemp._destroy = $event"
          @remove="removeReferringHistoryTemp($event)"
        />

        <div class="d-flex justify-center">
          <v-btn
            text
            color="primary"
            :ripple="false"
            :aria-label="`${currentClub.referralAlias}を追加`"
            @click="addReferringHistoryTemp({})"
          >
            <v-icon
              left
              small
            >
              {{ icons.mdiPlus }}
            </v-icon>
            {{ currentClub.referralAlias }}を追加
          </v-btn>
        </div>

        <div
          v-if="isChanging"
          class="mt-2"
        >
          <div class="w-full d-flex justify-space-around align-center px-2">
            <v-btn
              color="primary"
              :loading="isSubmitting"
              :disabled="!isValidToSubmit || isSubmitting"
              :ripple="false"
              @click="updateReferringHistories"
            >
              更新
            </v-btn>
            <v-btn
              color="secondary"
              text
              small
              :ripple="false"
              class="ml-4"
              aria-label="編集をキャンセル"
              @click="seedFromServer"
            >
              キャンセル
            </v-btn>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import {
  inject,
  ref,
  computed,
  watch,
  getCurrentInstance,
  onMounted,
  onUnmounted,
} from '@vue/composition-api'
import { each, map, cloneDeep } from 'lodash'
import { mdiPlus } from '@mdi/js'
import useReferral from '@/views/composable/useReferral'
import useCurrentData from '@/views/composable/useCurrentData'
import ReferringHistoryApi from '@/api/waiter/ReferringHistory'
import ReferralCard from '@/views/components/table/ReferralCard.vue'

export default {
  components: {
    ReferralCard,
  },
  props: {
    tableId: {
      type: [Number, String],
      validator: value => !Number.isNaN(Number(value)),
      required: true,
    },
  },
  setup(props, { emit }) {
    const vm = getCurrentInstance().proxy

    const referringHistories = inject('referringHistories', [])
    const referrals = inject('referrals', [])
    const users = inject('users', [])

    const isSubmitting = ref(false)
    const seededSnapshot = ref('[]')

    const {
      referringHistoriesTemp,
      addReferringHistoryTemp,
      removeReferringHistoryTemp,
      resetReferringHistoriesTemp,
      isValidToSubmit,
    } = useReferral()
    const { currentClub } = useCurrentData()

    const snapshotOf = temps => {
      return JSON.stringify(map(temps, temp => ({
        id: temp.id,
        userId: temp.userId,
        referralId: temp.referralId,
        // eslint-disable-next-line no-underscore-dangle
        destroy: !!temp._destroy,
      })))
    }

    // サーバー状態からtempを組み直す（初期表示・キャンセル・更新反映後）
    const seedFromServer = () => {
      resetReferringHistoriesTemp()
      each(referringHistories.value, referringHistory => {
        addReferringHistoryTemp({
          id: referringHistory.id,
          userId: referringHistory.attributes.user.data.id,
          referralId: referringHistory.attributes.referral.data.id,
        })
      })
      seededSnapshot.value = snapshotOf(referringHistoriesTemp.value)
    }

    // NOTE: 追加/削除だけでなく既存行の担当・契機の変更もスナップショット比較で検知する
    const isChanging = computed(() => snapshotOf(referringHistoriesTemp.value) !== seededSnapshot.value)

    const updateReferringHistories = async () => {
      isSubmitting.value = true

      const res = await ReferringHistoryApi.updateReferringHistories({
        tableId: props.tableId,
        referringHistories: referringHistoriesTemp.value,
      })

      if (res?.data) {
        emit('updated:referring-histories', res.data.referringHistories.data)
        emit('updated:table', res.data.table.data)

        vm.$toast.success(`${currentClub.value.referralAlias}を更新しました`)
      } else {
        vm.$toast.error(`${currentClub.value.referralAlias}の更新に失敗しました`)
      }

      isSubmitting.value = false
      seedFromServer()
    }

    // NOTE: 更新反映や他端末の変更でサーバー状態が変わったら組み直す。
    // 編集途中(isChanging)の入力はWebSocket更新で壊さない
    watch(() => cloneDeep(referringHistories.value), () => {
      if (isChanging.value && !isSubmitting.value) return

      seedFromServer()
    })

    onMounted(() => seedFromServer())
    onUnmounted(() => resetReferringHistoriesTemp())

    return {
      referringHistories,
      referrals,
      users,

      isChanging,
      isSubmitting,

      referringHistoriesTemp,
      removeReferringHistoryTemp,
      addReferringHistoryTemp,
      isValidToSubmit,
      updateReferringHistories,
      seedFromServer,

      icons: {
        mdiPlus,
      },
    }
  },
}
</script>
